import type { DataOption } from '@/common.types'
import type {
	BIResponse,
	FilterRuleModel,
	Metric,
	StatisticsInterpretabilityResponse,
} from '@/shared/generated'
import type { TFunction } from 'i18next'
import type {
	OptimizationGroupsType,
	SelectedOptimizationGroupType,
	ValueTypes,
} from '../lib/helpers'

import { useEffect, useMemo, useState } from 'react'
import { any, filter, isEqual, map, pipe, prop, sortedUniqBy } from 'lodash/fp'
import { AlertTriangleIcon, PlusIcon, XIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useMetricsQuery } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'
import {
	METRIC_PREFIX,
	MetricDataType,
	METRICS_PREFIX,
} from '@/entities/optimization-statistic'

import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/shared/components/ui/accordion'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox-native'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/shared/components/ui/command'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import {
	InputBase,
	InputBaseAdornmentButton,
	InputBaseFlexWrapper,
} from '@/shared/components/ui/input-base'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { useDisclosure } from '@/shared/hooks/use-disclosure'
import { intlFormat } from '@/shared/tools/dates'
import { formatMoney, formatNumber, NumberFormats } from '@/shared/tools/locale'

import {
	getForecastPeriod,
	getMetricCurrency,
	getMetricForecast,
	getMetricsAnalyticData,
	getPrefixedMetric,
	getTypesByMetricSchema,
	PROGRESS_TAB_METRICS,
} from '../lib/helpers'
import { useOptimizationsProgressAnalytics } from '../lib/useOptimizationsProgressAnalytics'
import MetricsPredictionChart from './metrics-prediction/MetricsPredictionChart'

export const ProgressTab = ({
	productFilters,
	optimizationGroups,
	isLoading: isDataLoading,
}: {
	productFilters: FilterRuleModel[]
	optimizationGroups: OptimizationGroupsType[]
	isLoading: boolean
}) => {
	const { t } = useTranslation()

	const currency = useSelectedCurrency()
	const [optimizationGroupsToCompare, setOptimizationGroupsToCompare] =
		useState<OptimizationGroupsType[]>([])
	useEffect(() => {
		setOptimizationGroupsToCompare([])
	}, [optimizationGroups])
	const showAdditionalMetrics = optimizationGroupsToCompare.length > 0
	const filters = useMemo(
		() => productFilters.filter(({ name }) => name !== 'optimization_group_id'),
		[productFilters]
	)
	const metricQuery = useMetricsQuery({
		select: pipe(filter(({ type }) => type === METRICS_PREFIX)),
	})
	const mainAnalytics = useOptimizationsProgressAnalytics({
		optimizationGroups: optimizationGroups,
		filters,
	})
	const additionalAnalytics = useOptimizationsProgressAnalytics({
		optimizationGroups: optimizationGroupsToCompare,
		filters,
		enabled: showAdditionalMetrics,
	})
	const mainMetrics = useMemo(
		() =>
			!!mainAnalytics.data &&
			enrichMetrics(
				{
					data: mainAnalytics.data as MetricAnalyticDataType,
					metricSchemes: metricQuery.data,
					chartColors: METRICS_COLORS.main,
					currency,
				},
				t
			),
		[mainAnalytics.data, metricQuery.data, currency, t]
	)
	const additionalMetrics = useMemo(
		() =>
			!!additionalAnalytics.data &&
			enrichMetrics(
				{
					data: additionalAnalytics.data as MetricAnalyticDataType,
					metricSchemes: metricQuery.data,
					chartColors: METRICS_COLORS.additional,
					currency,
				},
				t
			),
		[additionalAnalytics.data, metricQuery.data, currency, t]
	)
	const isLoading =
		any(prop('isLoading'), [mainAnalytics, additionalAnalytics, metricQuery]) ||
		isDataLoading
	const isError = any(prop('isError'), [
		mainAnalytics,
		additionalAnalytics,
		metricQuery,
	])
	const handleApply = (selected: SelectedOptimizationGroupType[]) => {
		const selectedOptimizationGroups = getOptimizationGroupsToCompare({
			selected,
			optimizationGroups,
		})
		setOptimizationGroupsToCompare(selectedOptimizationGroups)
	}
	if (isError) {
		return (
			<div className='flex h-full items-center justify-center px-4'>
				<Empty>
					<EmptyHeader>
						<EmptyMedia variant='icon'>
							<AlertTriangleIcon />
						</EmptyMedia>
						<EmptyTitle>{t('fatal_error_title')}</EmptyTitle>
						<EmptyDescription>{t('fatal_error_desc')}</EmptyDescription>
					</EmptyHeader>
				</Empty>
			</div>
		)
	}

	return (
		<div className='h-max w-full'>
			<ProgressTabHeader
				optimizationGroups={optimizationGroups}
				comparisonOgs={optimizationGroupsToCompare}
				handleClear={() => setOptimizationGroupsToCompare([])}
				handleApply={handleApply}
			/>
			<div className='p-4'>
				<Accordion
					defaultValue={PROGRESS_TAB_METRICS?.map(
						(metric, i) => `${metric}_${i}`
					)}
					type='multiple'
				>
					{PROGRESS_TAB_METRICS?.map((key, i) => {
						const mainAnalytic = (mainMetrics as MetricsAnalyticsType)?.[key]
						const metrics = showAdditionalMetrics
							? [
									mainAnalytic,
									(additionalMetrics as MetricsAnalyticsType)?.[key],
								]
							: [mainAnalytic]

						return (
							<AccordionItem value={`${key}_${i}`} key={`${key}_${i}`}>
								<AccordionTrigger>
									{t(key, { defaultValue: key })}
								</AccordionTrigger>
								<AccordionContent>
									{isLoading ? (
										<Skeleton className='h-60 w-full rounded-sm' />
									) : (
										<MetricsPredictionChart
											metrics={metrics.map((m) => ({
												name: m.name,
												data: m.data,
												metricValueTypes: m.metricValueTypes,
												currency: m.currency,
											}))}
										/>
									)}
								</AccordionContent>
							</AccordionItem>
						)
					})}
				</Accordion>
			</div>
		</div>
	)
}
export type MetricType = {
	data: [number, number][]
	name: string
	metricValueTypes: ValueTypes
	currency?: string
	color: string
	predictColor: (string | number)[][]
	analyticColor: (string | number)[][]
}

const getOptimizationGroupsToCompare = ({
	selected,
	optimizationGroups,
}: {
	selected: SelectedOptimizationGroupType[]
	optimizationGroups: OptimizationGroupsType[]
}) =>
	optimizationGroups?.filter(({ id }) =>
		selected.map(({ id }) => id).includes(id)
	)
const formatPoint = (point: Highcharts.Point) => {
	const currency = (point.series.options as { currency?: string }).currency

	return currency
		? formatMoney(point.y as number, currency, NumberFormats.Standart)
		: formatNumber(point.y as number)
}
export function formatter(this: Highcharts.Point) {
	const self = this as unknown as any
	const head = `<div class="mb-2.5"><small>${intlFormat(new Date(this.x as number))}</small></div>`

	const body = self.points
		?.map(
			(point: Highcharts.Point) => `<div class="flex justify-between">
				<div class="flex items-center">
					<div class="color-dot rounded-lg mr-2" style="background: ${point.color};"></div>
					<strong class="truncate">${point.series.name}</strong>
				</div>
				<div class="flex items-center">
					<strong class="flex items-center mr-2">${formatPoint(point)}</strong>
				</div>
			</div>`
		)
		.join('')

	return `<div class="chart-tooltip w-72">
		${head}
		${body}
	</div>`
}

const enrichMetrics = (
	{ data, metricSchemes, chartColors, currency }: EnrichMetricsArgsType,
	t: TFunction
): MetricsAnalyticsType => {
	/** 7 days period from revision date */
	const forecastPeriod = getForecastPeriod(data.revisionDate)
	/** metrics bi analytic  */
	const analyticsData = getMetricsAnalyticData(data.biAnalytic!)

	return PROGRESS_TAB_METRICS.reduce((acc, key) => {
		const prefixedKey = getPrefixedMetric({
			metric: key,
			prefix: METRIC_PREFIX,
		})
		const metricSchema = metricSchemes?.find(
			({ name }) => name.split('.')[0] === `${prefixedKey}`
		)
		const metricKey = `${prefixedKey}.final`
		/** 7 days prediction data for a metric */
		const forecastData =
			(data.interpretabilityStatistic?.metrics as Record<string, number>)?.[
				metricKey
			] || 0
		/** The 'final' value, returned for the margin metric, represents a raw value and needs to be multiplied by 100 to express it as a percentage */
		const metricValueTypes = getTypesByMetricSchema(metricSchema!)
		const isMetricDataTypePercent =
			metricValueTypes.dataType === MetricDataType.Percent
		/** Format metric data as a percentage if its type is 'percent' */
		const formattedForcastData = isMetricDataTypePercent
			? forecastData * 100
			: forecastData

		return {
			...acc,
			[key]: {
				name: t(`metric.${key}`),
				analyticKey: key,
				metricValueTypes,
				currency: getMetricCurrency(metricSchema, currency),
				data: [
					...(analyticsData?.find((analytic) => analytic.key === key)?.data ||
						[]),
					isMetricDataTypePercent
						? [
								forecastPeriod,
								formattedForcastData,
							] /**  prediction for all forecastPeriod*/
						: getMetricForecast(
								forecastPeriod,
								formattedForcastData
							) /** prediction for one day */,
				] as [number, number][],
				...chartColors,
			},
		}
	}, {})
}
export const ProgressTabHeader = ({
	optimizationGroups,
	handleClear,
	handleApply,
	comparisonOgs,
}: HeaderProps) => {
	const { t } = useTranslation()

	const [selected, setSelected] = useState<SelectedOptimizationGroupType[]>([])
	const isDisabled =
		isEqual(
			map(prop('id'), sortedUniqBy(prop('id'), selected)),
			map(prop('id'), sortedUniqBy(prop('id'), comparisonOgs))
		) || selected.length === 0
	useEffect(() => setSelected([]), [optimizationGroups])
	const dropdownOptions = useMemo(
		() =>
			optimizationGroups?.map(({ name, id }) => ({ value: id, label: name })) ||
			[],
		[optimizationGroups]
	)
	const handleChange = (
		optimizationGroups: { value: string; label: string }[]
	) => {
		const selected = optimizationGroups.map(({ value, label }) => ({
			name: label,
			id: value,
		}))
		setSelected(selected)
	}

	return (
		<div className='sticky-top flex items-center justify-between border-b px-4 py-2'>
			<div className='flex items-center'>
				<span className='mr-2 size-2 shrink-0 rounded-full bg-primary' />
				<p className='text-sm font-medium'>
					{t('opt.summary.selected_ogs', { defaultValue: 'Selected OG(s)' })}
				</p>
			</div>
			<ProgressTabHeaderDropdown
				isDisabled={isDisabled}
				values={selected.map(({ id }) => id)}
				handleClear={() => {
					setSelected([])
					handleClear()
				}}
				onApply={() => handleApply(selected)}
				options={dropdownOptions}
				handleChange={handleChange}
				totalOgsToCompare={comparisonOgs.length}
			/>
		</div>
	)
}
const ProgressTabHeaderDropdown = ({
	options,
	onApply,
	handleChange,
	values = [],
	handleClear,
	isDisabled,
	totalOgsToCompare,
}: DropdownProps) => {
	const { t } = useTranslation()

	const popover = useDisclosure()

	return (
		<Popover
			open={popover.isOpen}
			onOpenChange={(open) => (open ? popover.open() : popover.close())}
		>
			{totalOgsToCompare ? (
				<InputBase>
					<PopoverTrigger asChild>
						<InputBaseFlexWrapper>
							<div className='p-r flex items-center' typeof='button'>
								<span className='mr-2 size-2 shrink-0 rounded-full bg-purple-300' />
								<p className='text-sm font-normal'>
									{t('opt.summary.og_for_comparison', {
										defaultValue: 'OG for comparison',
									})}{' '}
									({totalOgsToCompare})
								</p>
							</div>
						</InputBaseFlexWrapper>
					</PopoverTrigger>
					<InputBaseAdornmentButton onClick={handleClear}>
						<XIcon />
					</InputBaseAdornmentButton>
				</InputBase>
			) : (
				<PopoverTrigger asChild>
					<Button variant='ghost'>
						<PlusIcon />
						{t('opt.summary.add_comperison', {
							defaultValue: 'Add Comparison',
						})}
					</Button>
				</PopoverTrigger>
			)}
			<PopoverContent align='end' side='bottom' className='w-80 p-0 text-sm'>
				<Command>
					<CommandInput
						placeholder={t('general_search')}
						autoFocus
						className='h-9'
					/>
					<CommandList>
						<CommandEmpty>
							{t('general_no_results_found', {
								defaultValue: 'No results found',
							})}
						</CommandEmpty>
						<CommandGroup>
							{options.map(({ value, label }) => (
								<CommandItem
									key={value}
									value={value}
									onSelect={(currentValue) => {
										const newValues = values.some(
											(value) => value === currentValue
										)
											? values.filter((value) => value !== currentValue)
											: [...values, currentValue]
										handleChange(
											options.filter(({ value }) => newValues.includes(value))
										)
									}}
								>
									<Checkbox checked={values.includes(value)} />
									{label}
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
				<div className='flex justify-end space-x-2 border-t px-4 py-2'>
					<Button size='sm' onClick={popover.close}>
						{t('general_cancel')}
					</Button>
					<Button
						disabled={isDisabled}
						variant='primary'
						size='sm'
						onClick={() => {
							onApply()
							popover.close()
						}}
					>
						{t('general_apply')}
					</Button>
				</div>
			</PopoverContent>
		</Popover>
	)
}

type EnrichMetricsArgsType = {
	data: MetricAnalyticDataType
	metricSchemes: Metric[]
	currency?: string
	chartColors: {
		color: string
		predictColor: (string | number)[][]
		analyticColor: (string | number)[][]
	}
}
type MetricsAnalyticsType = Record<string, MetricType>
type HeaderProps = {
	optimizationGroups: SelectedOptimizationGroupType[]
	handleClear: () => void
	handleApply: (selected: SelectedOptimizationGroupType[]) => void
	comparisonOgs: OptimizationGroupsType[]
}
type DropdownProps = {
	options: DataOption[]
	onApply: () => void
	handleChange: (value: { value: string; label: string }[]) => void
	values?: string[]
	handleClear: () => void
	isDisabled: boolean
	totalOgsToCompare: number
}
type MetricAnalyticDataType = {
	biAnalytic: BIResponse
	revisionDate: string
	interpretabilityStatistic: StatisticsInterpretabilityResponse
}
const METRICS_COLORS = {
	main: {
		color: '#32B2F3',
		predictColor: [
			[0, 'rgba(50, 178, 243, 0.32)'],
			[1, 'rgba(108, 163, 191, 0)'],
		],
		analyticColor: [
			[0, 'rgba(50, 178, 243, 0.72)'],
			[1, 'rgba(118, 162, 184, 0)'],
		],
	},
	additional: {
		color: '#8F98FF',
		predictColor: [
			[0, 'rgba(143, 152, 255, 0.32)'],
			[1, 'rgba(143, 152, 255, 0)'],
		],
		analyticColor: [
			[0, 'rgba(143, 152, 255, 0.72)'],
			[1, 'rgba(143, 152, 255, 0)'],
		],
	},
}
